import { useEffect } from 'react';

/**
 * A deliberately silly, one-shot celebration overlay: a burst of confetti from
 * the bottom corners plus a random mascot flying across the screen.
 *
 * This is the one place in the app that breaks design.md's "no playful
 * animations" rule, and it does so on purpose — it fires only on a genuine win
 * (an invoice going out the door), never on navigation or routine feedback.
 * Keep it to wins; if it starts showing up on ordinary interactions it stops
 * being fun and just becomes noise.
 *
 * Usage — bump `trigger` to fire. It self-clears when the animation finishes,
 * so there is no `onDone` to plumb:
 *
 *   const [cheer, setCheer] = useState(0);
 *   ...
 *   setCheer(c => c + 1);
 *   <Celebration trigger={cheer} />
 *
 * Everything is drawn on one canvas in a single rAF loop, so there are no CSS
 * keyframes to collide with the global `prefers-reduced-motion` override in
 * index.css — which matters, because that override zeroes animation durations
 * rather than removing elements, and would otherwise leave a unicorn parked
 * mid-screen. Under reduced motion nothing is mounted at all.
 *
 * The overlay canvas is created and removed imperatively rather than rendered,
 * because it is a transient decoration with no place in the React tree: that
 * keeps it out of the component's render output entirely, so a re-trigger or an
 * unmount mid-flight is just a DOM node going away.
 */

const MASCOTS = ['🦄', '🚀', '💸', '🎉', '🏆', '🛸', '🐐'];

// Brand palette, plus enough gold and bright aqua to read as festive.
const COLORS = ['#3D8A8C', '#7ED4D6', '#172A3C', '#C98A13', '#2E7D5B', '#B22020', '#D8E8E8'];

const DURATION_MS = 2800;
const FLYBY_MS = 2200;
// Physics is tuned per 60Hz frame and scaled by the real time step below.
const FRAME_MS = 1000 / 60;
const GRAVITY = 0.16;
const DRAG = 0.994;

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** Two cannons at the bottom corners, firing up and inward. */
function spawnConfetti(width, height) {
  const perCannon = width < 640 ? 55 : 90;
  const particles = [];

  for (const side of [-1, 1]) {
    const originX = side === -1 ? 0 : width;
    for (let i = 0; i < perCannon; i += 1) {
      // Aim inward and up, with a wide spread so it reads as a burst, not a jet.
      // `spread` is radians off vertical; side -1 is the left cannon, so it
      // fires rightward (+vx) and side 1 fires leftward.
      const spread = 0.15 + Math.random() * 0.9;
      const speed = 11 + Math.random() * 13;
      particles.push({
        x: originX,
        y: height,
        vx: -side * Math.sin(spread) * speed,
        vy: -Math.cos(spread) * speed,
        w: 5 + Math.random() * 6,
        h: 8 + Math.random() * 7,
        rotation: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.32,
        color: pick(COLORS),
      });
    }
  }

  return particles;
}

export default function Celebration({ trigger = 0 }) {
  useEffect(() => {
    if (!trigger) return undefined;
    if (prefersReducedMotion()) return undefined;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    canvas.setAttribute('aria-hidden', 'true');
    canvas.className = 'fixed inset-0 z-[9998] pointer-events-none';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    document.body.appendChild(canvas);

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = window.innerWidth;
    const height = window.innerHeight;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    ctx.scale(dpr, dpr);

    const particles = spawnConfetti(width, height);
    const mascot = pick(MASCOTS);
    const mascotSize = Math.max(44, Math.min(96, width * 0.09));
    const mascotY = height * (0.24 + Math.random() * 0.28);

    let frame = 0;
    let start = null;
    let last = null;

    const draw = (now) => {
      if (start === null) start = now;
      if (last === null) last = now;
      const elapsed = now - start;
      // Integrate against real elapsed time rather than frame count, so the
      // burst looks the same at 60Hz, at 120Hz, and on a throttled tab. Capped
      // so a long stall (backgrounded tab) resumes instead of teleporting.
      const step = Math.min((now - last) / FRAME_MS, 3);
      last = now;
      const drag = DRAG ** step;
      ctx.clearRect(0, 0, width, height);

      // Confetti holds full opacity for the first half, then fades out.
      const fade = 1 - (elapsed - DURATION_MS * 0.5) / (DURATION_MS * 0.5);
      ctx.globalAlpha = Math.max(0, Math.min(1, fade));

      for (const p of particles) {
        p.vy += GRAVITY * step;
        p.vx *= drag;
        p.vy *= drag;
        p.x += p.vx * step;
        p.y += p.vy * step;
        p.rotation += p.spin * step;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }

      if (elapsed < FLYBY_MS) {
        // Straight across the viewport with a gentle bob and matching tilt.
        const t = elapsed / FLYBY_MS;
        const bobPhase = Math.sin(t * Math.PI * 3);
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.translate(-0.15 * width + t * 1.3 * width, mascotY + bobPhase * mascotSize * 0.35);
        ctx.rotate(bobPhase * 0.12);
        ctx.font = `${mascotSize}px system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(mascot, 0, 0);
        ctx.restore();
      }

      if (elapsed < DURATION_MS) {
        frame = requestAnimationFrame(draw);
      } else {
        canvas.remove();
      }
    };

    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      canvas.remove();
    };
  }, [trigger]);

  return null;
}
